import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import App from './App';
import OrgBrandingApplier from './shared/components/common/OrgBrandingApplier';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import { getNavDeepLinkPaths } from './shared/utils/navDeepLink';

// Global error handler to suppress non-critical audio errors
const originalError = console.error;
const originalWarn = console.warn;

// Suppress audio-related errors globally
console.error = function(...args) {
  const errorMsg = args[0]?.toString() || '';
  const errorStr = args[0]?.message || errorMsg;

  // Suppress audio playback errors - these are non-critical for the app
  if (errorMsg.includes('play()') ||
      errorMsg.includes('media was removed') ||
      errorStr.includes('play()') ||
      errorStr.includes('media was removed') ||
      errorMsg.includes('NotAllowedError') ||
      errorMsg.includes('NotSupportedError') ||
      errorMsg.includes('AbortError') ||
      errorMsg.includes('The play() request was interrupted')) {
    return; // Silently ignore audio-related errors
  }

  originalError.apply(console, args);
};

console.warn = function(...args) {
  const warnMsg = args[0]?.toString() || '';

  // Suppress audio-related warnings
  if (warnMsg.includes('audio') || warnMsg.includes('beep')) {
    return;
  }

  originalWarn.apply(console, args);
};

// Handle unhandled promise rejections from audio playback
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const reasonStr = (reason?.message || reason?.toString() || '').toString();

  // Suppress audio-related promise rejections
  if (reasonStr.includes('play()') ||
      reasonStr.includes('media was removed') ||
      reasonStr.includes('NotAllowedError') ||
      reasonStr.includes('NotSupportedError') ||
      reasonStr.includes('AbortError') ||
      reasonStr.includes('The play() request was interrupted')) {
    event.preventDefault(); // Prevent error from being displayed
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // Temporarily disabled StrictMode to see production behavior
  //<React.StrictMode>
    <BrowserRouter>
      <OrgBrandingApplier />
      <Routes>
        {/* Application routes */}
        <Route path="/" element={<App />} />
        <Route path="/login" element={<App />} />
        <Route path="/verify-email" element={<App />} />
        <Route path="/reactivate" element={<App />} />
        <Route path="/password-reset-required" element={<App />} />
        <Route path="/locations/:id" element={<App />} />
        <Route path="/privacy" element={<App />} />
        <Route path="/terms" element={<App />} />
        <Route path="/support" element={<App />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public routes - must come before the catch-all route */}
        <Route path="/device-scan/*" element={<App />} />
        <Route path="/complaint-confirmation" element={<App />} />
        {/* Navbar deep links — must be before catch-all so /alerts etc. are not redirected to / */}
        {getNavDeepLinkPaths().map((path) => (
          <Route key={path} path={path} element={<App />} />
        ))}
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  //</React.StrictMode>
);

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('Service Worker registered successfully:', registration);
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
  });

  // Check for service worker updates periodically
  setInterval(() => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.update();
      });
    });
  }, 60000); // Check every 60 seconds
}

// Enable PWA install prompt handling
let deferredPrompt;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.log('Install prompt is available');
  // You can store this to show a custom install button if desired
  window.PWAInstallPrompt = deferredPrompt;
});

// Handle app installation
window.addEventListener('appinstalled', () => {
  console.log('DevicePulse app has been installed');
  deferredPrompt = null;
});

// Handle app launch
window.addEventListener('beforeuninstallprompt', () => {
  console.log('App is about to be uninstalled');
});
